/**
 * ConsentMain Component
 *
 * Admin view for 電子交付同意一覧: one row per employee (latest consent across 本社/現場).
 * Search by name or ID; CSV uses same data + date range.
 *
 * @component
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import './css/ConsentMain.css';
import {
  getConsentAgreedList,
  getConsentNameSuggestions,
  downloadConsentCsv,
  type ConsentAgreedRow,
} from '../../services/ConsentService';

export const ConsentMain: React.FC<{
  /** 管理者: open global consent template settings */
  onOpenFormSettings?: () => void;
}> = ({ onOpenFormSettings }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [list, setList] = useState<ConsentAgreedRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [csvDownloading, setCsvDownloading] = useState(false);
  const [csvError, setCsvError] = useState<string | null>(null);
  const suggestionRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const fetchList = useCallback(async (search?: string) => {
    setLoading(true);
    try {
      const data = await getConsentAgreedList(search);
      setList(data);
    } catch {
      setList([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setSearchQuery('');
    setSuggestions([]);
    setShowSuggestions(false);
    fetchList();
  }, [fetchList]);

  useEffect(() => {
    const t = searchQuery.trim();
    if (t.length === 0) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    const timer = setTimeout(() => {
      getConsentNameSuggestions(t)
        .then((names) => {
          setSuggestions(names);
          setShowSuggestions(names.length > 0);
        })
        .catch(() => {
          setSuggestions([]);
          setShowSuggestions(false);
        });
    }, 200);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleSearch = () => {
    setShowSuggestions(false);
    fetchList(searchQuery.trim() || undefined);
  };

  const handleClear = () => {
    setSearchQuery('');
    setShowSuggestions(false);
    fetchList();
  };

  const handleSelectSuggestion = (name: string) => {
    setSearchQuery(name);
    setShowSuggestions(false);
    fetchList(name);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const handleDownloadCsv = async () => {
    const from = fromDate.trim();
    const to = toDate.trim();
    if (!from || !to) {
      setCsvError('開始日と終了日を両方選択してください。');
      return;
    }
    if (new Date(from) > new Date(to)) {
      setCsvError('開始日は終了日より前を選択してください。');
      return;
    }
    setCsvError(null);
    setCsvDownloading(true);
    try {
      await downloadConsentCsv(from, to);
    } catch {
      setCsvError('CSVのダウンロードに失敗しました。');
    } finally {
      setCsvDownloading(false);
    }
  };

  useEffect(() => {
    const onOutside = (e: MouseEvent) => {
      if (
        suggestionRef.current &&
        inputRef.current &&
        !suggestionRef.current.contains(e.target as Node) &&
        !inputRef.current.contains(e.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, []);

  const formatTime = (isoTime: string) => {
    if (!isoTime) return '—';
    const part = isoTime.split(':');
    if (part.length >= 2) return `${part[0]}:${part[1]}`;
    return isoTime;
  };

  const statusLabel = (status: string | undefined) => {
    if (status === 'agree') return '同意';
    if (status === 'disagree') return '不同意';
    return status ?? '—';
  };

  return (
    <div className="consent-main main-content">
      <div className="consent-main-title-row">
        <h3>電子交付同意一覧</h3>
        {onOpenFormSettings && (
          <button
            type="button"
            className="consent-main-settings-btn"
            onClick={onOpenFormSettings}
            title="同意書の本文・日付を設定"
          >
            ⚙️ 同意書設定
          </button>
        )}
      </div>

      <div className="tab-content">
        <div className="tab-panel">
          <div className="consent-tab-panel">
            <div className="consent-search-section">
              <h4>最新の回答一覧（名前またはIDで検索）</h4>
              <div className="consent-export-row">
                <label className="consent-export-label">
                  <span>開始日</span>
                  <input
                    type="date"
                    className="consent-date-input"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    aria-label="開始日"
                  />
                </label>
                <label className="consent-export-label">
                  <span>終了日</span>
                  <input
                    type="date"
                    className="consent-date-input"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    aria-label="終了日"
                  />
                </label>
                <button
                  type="button"
                  className="consent-csv-btn"
                  onClick={handleDownloadCsv}
                  disabled={csvDownloading}
                >
                  {csvDownloading ? 'ダウンロード中...' : '📥 CSVダウンロード'}
                </button>
              </div>
              {csvError && <p className="consent-csv-error" role="alert">{csvError}</p>}
              <div className="consent-search-controls">
                <div className="consent-search-input-wrap">
                  <input
                    ref={inputRef}
                    type="text"
                    className="consent-search-input"
                    placeholder="名前またはIDを入力..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                  />
                  {showSuggestions && suggestions.length > 0 && (
                    <div ref={suggestionRef} className="consent-suggestions">
                      {suggestions.map((name) => (
                        <button
                          key={name}
                          type="button"
                          className="consent-suggestion-item"
                          onClick={() => handleSelectSuggestion(name)}
                        >
                          {name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  className="consent-search-btn"
                  onClick={handleSearch}
                  disabled={loading}
                >
                  {loading ? '検索中...' : '🔍 検索'}
                </button>
                <button
                  type="button"
                  className="consent-clear-btn"
                  onClick={handleClear}
                  disabled={loading}
                >
                  クリア
                </button>
              </div>
            </div>

            <div className="consent-table-wrap">
              {loading ? (
                <p className="consent-loading">読み込み中...</p>
              ) : list.length === 0 ? (
                <p className="consent-empty">該当するデータがありません。</p>
              ) : (
                <table className="consent-table">
                  <thead>
                    <tr>
                      <th>従業員ID</th>
                      <th>氏名</th>
                      <th>ステータス</th>
                      <th>回答日</th>
                      <th>回答時刻</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((row) => (
                      <tr key={row.empId}>
                        <td>{row.empId}</td>
                        <td>{row.name}</td>
                        <td>{statusLabel(row.status)}</td>
                        <td>{row.consentDate}</td>
                        <td>{formatTime(row.consentTime)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
